import type { OwnedWallet } from './types';

const ADDR_RE = /^0x[a-fA-F0-9]{40}$/;

function normalizeAddr(a: string): string | null {
  const t = a.trim().toLowerCase();
  return ADDR_RE.test(t) ? t : null;
}

/**
 * One line: `0xabc…` or `Label|0xabc…` (label may contain `|` only if we take last segment as address — keep simple: split on first `|`).
 */
export function parseWalletLine(line: string): OwnedWallet | null {
  const raw = line.trim();
  if (!raw || raw.startsWith('#')) return null;
  const pipe = raw.indexOf('|');
  if (pipe >= 0) {
    const label = raw.slice(0, pipe).trim() || 'Wallet';
    const addr = normalizeAddr(raw.slice(pipe + 1));
    if (!addr) return null;
    return { address: addr, label };
  }
  const addr = normalizeAddr(raw);
  if (!addr) return null;
  return { address: addr, label: 'Wallet' };
}

/**
 * Primary field (single 0x) plus optional multiline textarea (extra owned addresses).
 */
export function buildOwnedWalletList(
  primaryAddressRaw: string,
  extraLinesText?: string
): { wallets: OwnedWallet[]; error?: string } {
  const primary = normalizeAddr(primaryAddressRaw);
  if (!primary) {
    return { wallets: [], error: 'Primary address must be a valid 0x… (42 chars).' };
  }
  const seen = new Set<string>();
  const wallets: OwnedWallet[] = [];

  const add = (w: OwnedWallet) => {
    if (seen.has(w.address)) return;
    seen.add(w.address);
    wallets.push(w);
  };

  add({ address: primary, label: 'Primary' });

  if (extraLinesText?.trim()) {
    for (const line of extraLinesText.split(/\r?\n/)) {
      const w = parseWalletLine(line);
      if (w) add(w);
    }
  }

  return { wallets };
}

/**
 * Multiline list of **CEX deposit / hot wallet** addresses (shared exchange custody — not fetched as owned).
 * Same line format as owned wallets: `Label|0x…` or `0x…`.
 */
export function buildCexDepositWalletList(text: string): OwnedWallet[] {
  const out: OwnedWallet[] = [];
  const seen = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const w = parseWalletLine(line);
    if (w && !seen.has(w.address)) {
      seen.add(w.address);
      out.push(w);
    }
  }
  return out;
}

export function labelForAddress(
  wallets: OwnedWallet[],
  addressLower: string
): string {
  const w = wallets.find((x) => x.address === addressLower);
  return w?.label ?? addressLower.slice(0, 10) + '…';
}
